import React, { createContext, FunctionComponent, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Features } from '../../model/features'
import { useReadyFeatureFlag } from '../../state/featureFlags/hooks'
import { useLaunchDarklyIdentityReady } from '../../providers/CheckoutLaunchDarklyContext'
import {
  createOwnershipReady,
  createOwnershipResponse,
  MOBILE_OWNERSHIP_READY_TIMEOUT_MS,
  OwnershipOffer,
  parseOwnershipCommand,
} from './mobileOwnershipProtocol'

export type CheckoutJourneyOwner = 'negotiating' | 'rocketship' | 'afterpay-mobile'
export type CheckoutJourneyStartBoundary = Readonly<{
  journeyId?: string
  startedAtEpochMs?: number
}>
export type CheckoutJourneyOwnership = Readonly<{
  owner: CheckoutJourneyOwner
  didFallbackFromMobileNegotiation: boolean
  journeyId?: string
  startedAtEpochMs?: number
  claimRocketshipOwnership: () => CheckoutJourneyStartBoundary | undefined
  canStartRocketshipJourney: () => boolean
  isRocketshipOwner: () => boolean
}>

declare global {
  interface Window {
    __afterpayMobileUserJourneyV1?: boolean
  }
}

const claimUnavailable = (): undefined => undefined
const CheckoutJourneyOwnershipContext = createContext<CheckoutJourneyOwnership>({
  owner: 'rocketship',
  didFallbackFromMobileNegotiation: false,
  claimRocketshipOwnership: claimUnavailable,
  canStartRocketshipJourney: () => true,
  isRocketshipOwner: () => true,
})

const hasMobileOwnershipCapability = (): boolean =>
  window.__afterpayMobileUserJourneyV1 === true && typeof window.AfterpayReactNativeWebView?.postMessage === 'function'

export const MobileUserJourneyOwnership: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const featureFlagsReady = useLaunchDarklyIdentityReady()
  const enabled = useReadyFeatureFlag<boolean>(Features.MOBILE_USER_JOURNEY_OWNERSHIP, false)
  const mobileCapable = useRef(typeof window !== 'undefined' && hasMobileOwnershipCapability()).current
  const initialOwner = mobileCapable ? 'negotiating' : 'rocketship'
  const [owner, setOwner] = useState<CheckoutJourneyOwner>(initialOwner)
  const ownerRef = useRef<CheckoutJourneyOwner>(initialOwner)
  const offerRef = useRef<OwnershipOffer | undefined>(undefined)
  const acknowledgedRef = useRef(false)
  const claimedForCloseRef = useRef(false)
  const negotiationEnabledRef = useRef(false)
  const fallbackFromNegotiationRef = useRef(false)
  const eligibilityResolvedRef = useRef(!mobileCapable)
  const fallbackRef = useRef<number | undefined>(undefined)

  const clearFallback = useCallback((): void => {
    if (fallbackRef.current !== undefined) {
      window.clearTimeout(fallbackRef.current)
      fallbackRef.current = undefined
    }
  }, [])

  const resolveRocketshipOwnership = useCallback((): void => {
    fallbackFromNegotiationRef.current ||= negotiationEnabledRef.current
    clearFallback()
    negotiationEnabledRef.current = false
    ownerRef.current = 'rocketship'
    setOwner('rocketship')
  }, [clearFallback])

  const armFallback = useCallback((): void => {
    clearFallback()
    fallbackRef.current = window.setTimeout(resolveRocketshipOwnership, MOBILE_OWNERSHIP_READY_TIMEOUT_MS)
  }, [clearFallback, resolveRocketshipOwnership])

  const claimRocketshipOwnership = useCallback((): CheckoutJourneyStartBoundary | undefined => {
    if (ownerRef.current !== 'negotiating' || acknowledgedRef.current) return undefined

    const offer = offerRef.current
    claimedForCloseRef.current = true
    resolveRocketshipOwnership()
    return offer ? { journeyId: offer.checkoutFlowToken, startedAtEpochMs: offer.payload.startedAtEpochMs } : {}
  }, [resolveRocketshipOwnership])
  const canStartRocketshipJourney = useCallback((): boolean => !claimedForCloseRef.current, [])
  const isRocketshipOwner = useCallback((): boolean => ownerRef.current === 'rocketship', [])

  const post = useCallback((message: unknown): boolean => {
    try {
      const bridge = window.AfterpayReactNativeWebView
      if (typeof bridge?.postMessage !== 'function') return false
      bridge.postMessage(JSON.stringify(message), window.location.origin)
      return true
    } catch {
      return false
    }
  }, [])

  useEffect(() => {
    if (!mobileCapable) return undefined

    armFallback()
    const onMessage = (event: MessageEvent): void => {
      if (!negotiationEnabledRef.current || event.origin !== window.location.origin || event.source !== window) return
      const { data } = event
      const command = parseOwnershipCommand(data)
      if (command === undefined) return

      if (command.type === 'ownership_offer') {
        if (ownerRef.current === 'rocketship') return
        if (offerRef.current !== undefined && offerRef.current.checkoutFlowToken !== command.checkoutFlowToken) return
        offerRef.current = command
        if (post(createOwnershipResponse('ownership_ack', command.checkoutFlowToken)) && !acknowledgedRef.current) {
          acknowledgedRef.current = true
          armFallback()
        }
        return
      }

      if (offerRef.current?.checkoutFlowToken !== command.checkoutFlowToken) return
      if (ownerRef.current === 'rocketship') return
      clearFallback()
      ownerRef.current = 'afterpay-mobile'
      setOwner('afterpay-mobile')
      post(createOwnershipResponse('ownership_committed', command.checkoutFlowToken))
    }

    window.addEventListener('message', onMessage)
    return () => {
      clearFallback()
      window.removeEventListener('message', onMessage)
    }
  }, [armFallback, clearFallback, mobileCapable, post])

  useEffect(() => {
    if (!mobileCapable || !featureFlagsReady || eligibilityResolvedRef.current || ownerRef.current !== 'negotiating') return

    eligibilityResolvedRef.current = true
    if (!enabled) {
      resolveRocketshipOwnership()
      return
    }

    negotiationEnabledRef.current = true
    if (!post(createOwnershipReady())) {
      resolveRocketshipOwnership()
    }
  }, [enabled, featureFlagsReady, mobileCapable, post, resolveRocketshipOwnership])

  const value = useMemo(
    () =>
      owner === 'rocketship' && offerRef.current
        ? {
            owner,
            didFallbackFromMobileNegotiation: fallbackFromNegotiationRef.current,
            journeyId: offerRef.current.checkoutFlowToken,
            startedAtEpochMs: offerRef.current.payload.startedAtEpochMs,
            claimRocketshipOwnership,
            canStartRocketshipJourney,
            isRocketshipOwner,
          }
        : {
            owner,
            didFallbackFromMobileNegotiation: fallbackFromNegotiationRef.current,
            claimRocketshipOwnership,
            canStartRocketshipJourney,
            isRocketshipOwner,
          },
    [canStartRocketshipJourney, claimRocketshipOwnership, isRocketshipOwner, owner]
  )
  return <CheckoutJourneyOwnershipContext.Provider value={value}>{children}</CheckoutJourneyOwnershipContext.Provider>
}

export const useCheckoutJourneyOwner = (): CheckoutJourneyOwner => useContext(CheckoutJourneyOwnershipContext).owner
export const useCheckoutJourneyOwnership = (): CheckoutJourneyOwnership => useContext(CheckoutJourneyOwnershipContext)
